import { useState, useEffect, useCallback } from 'react';
import { safeGet, safeSet, getIranNow, getIranDayOfWeek, DAYS_OF_WEEK } from '../data/constants';

/* ══════════════════════════════════════════════════
   useNotifications
   مدیریت pin + notification + ارسال به Service Worker
══════════════════════════════════════════════════ */
export function useNotifications() {
  // 🟢 اصلاح مهم: بررسی وجود Notification در آبجکت window برای جلوگیری از کرش
  const [permission, setPermission] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'default';
  });
  
  const [pinnedIds, setPinnedIds] = useState(() => safeGet('aa_pins', []));
  const [alertedIds, setAlertedIds] = useState(() => safeGet('aa_alerts', []));
  const [swReady, setSwReady] = useState(false);

  /* ── ثبت SW ────────────────────────────────── */
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => setSwReady(true)).catch(console.error);
    }
  }, []);

  /* ── ارسال لیست جلسات به SW هر بار که تغییر کرد */
  const syncToSW = useCallback((meetings) => {
    if (!swReady || !meetings?.length || !('serviceWorker' in navigator)) return;
    
    navigator.serviceWorker.ready.then(reg => {
      try {
        reg.active?.postMessage({ type: 'SET_ALERTS', meetings });
      } catch (err) {
        console.error('خطا در ارسال پیام به Service Worker:', err);
      }
    });
  }, [swReady]);

  /* ── درخواست permission ────────────────────── */
  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    if (Notification.permission === 'granted') { 
      setPermission('granted'); 
      return true; 
    }
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('خطا در دریافت مجوز اعلان:', error);
      return false;
    }
  }, []);

  /* ── Toggle pin ─────────────────────────────── */
  const togglePin = useCallback((meetingId) => {
    setPinnedIds(prev => {
      const next = prev.includes(meetingId)
        ? prev.filter(id => id !== meetingId)
        : [...prev, meetingId];
      safeSet('aa_pins', next);
      return next;
    });
  }, []);

  /* ── Toggle alert ───────────────────────────── */
  const toggleAlert = useCallback(async (meeting) => {
    const granted = await requestPermission();
    if (!granted) {
      alert('برای دریافت اعلان، اجازه notification را در تنظیمات مرورگر خود فعال کنید.');
      return false;
    }
    
    setAlertedIds(prev => {
      const has = prev.includes(meeting.id);
      const next = has ? prev.filter(id => id !== meeting.id) : [...prev, meeting.id];
      safeSet('aa_alerts', next);
      return next;
    });
    return true;
  }, [requestPermission]);

  return {
    permission,
    pinnedIds,
    alertedIds,
    swReady,
    isPinned: id => pinnedIds.includes(id),
    isAlerted: id => alertedIds.includes(id),
    togglePin,
    toggleAlert,
    requestPermission,
    syncToSW,
  };
}

/* ══════════════════════════════════════════════════
   useAlarmSync
   وقتی meetings لود شد، جلسات alerted را به SW بفرست
══════════════════════════════════════════════════ */
export function useAlarmSync(meetings, alertedIds, syncToSW) {
  useEffect(() => {
    if (!meetings?.length || !alertedIds?.length) return;

    const now = getIranNow();
    const dow = getIranDayOfWeek(now);

    const toAlert = meetings
      .filter(m => alertedIds.includes(m.id))
      .map(m => {
        const todaySch = (m.schedules || []).find(s => Number(s.day_of_week) === dow);
        const startTime = todaySch?.start_time || m.first_start_time || null;
        return {
          id: m.id,
          title: m.title,
          type: m.type,
          days_array: m.days_array || [],
          todayStartTime: startTime ? startTime.substring(0, 5) : null,
          location_name: m.location_name,
          address: m.address,
          meeting_link: m.meeting_link,
        };
      })
      .filter(m => m.todayStartTime);

    if (toAlert.length) syncToSW(toAlert);
  }, [meetings, alertedIds, syncToSW]);
}

/* ══════════════════════════════════════════════════
   NotificationSetup
   نمایش وضعیت notification + دکمه فعال‌سازی
   (💡 پیشنهاد: بهتر است این کامپوننت را در آینده به فایل جداگانه‌ای در پوشه components منتقل کنید)
══════════════════════════════════════════════════ */
export function NotificationSetup({ alertedCount = 0 }) {
  // 🟢 ایمن‌سازی وضعیت اولیه
  const [perm, setPerm] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'default';
  });
  const [show, setShow] = useState(false);

  useEffect(() => {
    // 🟢 اگر کلا نوتیفیکیشن ساپورت نمی‌شود، هیچ‌وقت بنر را نشان نده
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    if (perm !== 'granted') {
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }
  }, [perm]);

  if (perm === 'granted' || !show || typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }

  const request = async () => {
    try {
      const result = await Notification.requestPermission();
      setPerm(result);
      if (result === 'granted') setShow(false);
    } catch (error) {
      console.error('خطا در درخواست مجوز:', error);
    }
  };

  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 14, left: 14, zIndex: 300,
      background: 'var(--bg3)', border: '1px solid rgba(99,179,237,.2)',
      borderRadius: 16, padding: '12px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 20px 60px rgba(0,0,0,.5)',
      animation: 'slideUp .35s ease',
    }}>
      <span style={{ fontSize: 24, flexShrink: 0 }}>🔔</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--text)', marginBottom: 2 }}>
          اعلان جلسات
        </div>
        <div style={{ fontSize: 11, color: 'var(--text2)' }}>
          قبل از شروع جلسه‌های پین‌شده، اعلان دریافت کنید
        </div>
      </div>
      <button onClick={request} style={{
        background: 'linear-gradient(135deg, #0369a1, #0ea5e9)',
        color: 'white', border: 'none', padding: '8px 14px',
        borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
        flexShrink: 0,
      }}>فعال‌سازی</button>
      <button onClick={() => setShow(false)} style={{
        color: 'var(--text2)', background: 'none', border: 'none',
        fontSize: 18, cursor: 'pointer', flexShrink: 0,
      }}>✕</button>
    </div>
  );
}